import { useState, useEffect } from "react";
import "./BlogForm.scss";
import { createBlog, updateBlog } from "../../api/blogs.api";

// 1. Importamos el nuevo componente BlogEditor
import BlogEditor from "./BlogEditor"; // Ajusta la ruta si es necesario

const BlogForm = ({ blogData, closeForm, refreshData }) => {
  const [formData, setFormData] = useState({
    blogTitle: "",
    blogDescription: "",
    blogContent: "",
    blogTags: "",
    blogSeoTitle: "",
    blogSeoDescription: "",
    blogImage: null,
  });

  const [preview, setPreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (blogData) {
      setFormData({
        blogTitle: blogData.title || "",
        blogDescription: blogData.description || "",
        blogContent: blogData.content || "",
        blogTags: blogData.tags ? blogData.tags.join(", ") : "",
        blogSeoTitle: blogData.seoTitle || "",
        blogSeoDescription: blogData.seoDescription || "",
        blogImage: null,
      });
      setPreview(blogData.image);
    }
  }, [blogData]);

  useEffect(() => {
    return () => {
      if (preview && preview.startsWith("blob:")) {
        URL.revokeObjectURL(preview);
      }
    };
  }, [preview]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    const dataToSend = new FormData();
    dataToSend.append("title", formData.blogTitle);
    dataToSend.append("description", formData.blogDescription);
    dataToSend.append("content", formData.blogContent);
    dataToSend.append("seoTitle", formData.blogSeoTitle);
    dataToSend.append("seoDescription", formData.blogSeoDescription);

    const tagsArray = formData.blogTags
      .split(",")
      .map((tag) => tag.trim())
      .filter((t) => t !== "");
    dataToSend.append("tags", JSON.stringify(tagsArray));

    if (formData.blogImage instanceof File) {
      dataToSend.append("image", formData.blogImage);
    }

    try {
      if (blogData?._id) {
        await updateBlog(blogData._id, dataToSend);
      } else {
        await createBlog(dataToSend);
      }
      refreshData();
      closeForm();
    } catch (error) {
      if (error.response) {
        console.error("Mensaje del servidor:", error.response.data.msg);
        console.error("Detalle del error:", error.response.data.error);
        console.error(
          "Objeto de error completo del backend:",
          error.response.data,
        );
      } else if (error.request) {
        console.error("El servidor no respondió:", error.request);
      } else {
        console.error("Error de configuración de Axios:", error.message);
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="BlogFormContainer">
      <form onSubmit={handleSubmit} className="BlogForm">
        <div className="BlogForm__grid">
          <div className="BlogForm__main">
            <div className="form-group">
              <label htmlFor="blogTitle">Título del Post</label>
              <input
                id="blogTitle"
                type="text"
                name="blogTitle"
                value={formData.blogTitle}
                onChange={(e) =>
                  setFormData({ ...formData, blogTitle: e.target.value })
                }
                required
                placeholder="Escriba aquí"
              />
            </div>

            <div className="form-group">
              <label htmlFor="blogContent">Cuerpo del Artículo</label>

              {/* 2. Reemplazamos Quill y los botones personalizados por el BlogEditor */}
              <div
                className="editor-wrapper"
                style={{ marginTop: "10px", width: "100%" }}
              >
                <BlogEditor
                  value={formData.blogContent}
                  onChange={(content) =>
                    setFormData({ ...formData, blogContent: content })
                  }
                />
              </div>
            </div>
          </div>

          <aside className="BlogForm__sidebar">
            <div className="form-group">
              <label htmlFor="blogImage">Imagen de Portada</label>
              <input
                id="blogImage"
                type="file"
                accept="image/*"
                required={!blogData}
                onChange={(e) => {
                  const file = e.target.files[0];
                  if (file) {
                    setFormData((prev) => ({ ...prev, blogImage: file }));
                    setPreview(URL.createObjectURL(file));
                  }
                }}
              />
              {preview && (
                <div className="image-preview" style={{ marginTop: "10px" }}>
                  <img
                    src={preview}
                    alt="Vista previa"
                    style={{
                      width: "100%",
                      borderRadius: "8px",
                      objectFit: "cover",
                    }}
                  />
                </div>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="blogDescription">Resumen para Listado</label>
              <textarea
                id="blogDescription"
                value={formData.blogDescription}
                onChange={(e) =>
                  setFormData({ ...formData, blogDescription: e.target.value })
                }
                rows="3"
                placeholder="Breve extracto del post"
              />
            </div>

            <div className="form-group">
              <label htmlFor="blogTags">Etiquetas (separadas por comas)</label>
              <input
                id="blogTags"
                type="text"
                name="blogTags"
                value={formData.blogTags}
                onChange={(e) =>
                  setFormData({ ...formData, blogTags: e.target.value })
                }
                placeholder="ej: residencial, patrimonial, ventas..."
              />
            </div>

            <div
              className="seo-extra"
              style={{
                background: "#f9f9f9",
                padding: "15px",
                borderRadius: "8px",
              }}
            >
              <h3 style={{ fontSize: "1rem", marginBottom: "10px" }}>
                Configuración SEO
              </h3>
              <div className="form-group">
                <label>Meta Title</label>
                <input
                  type="text"
                  value={formData.blogSeoTitle}
                  onChange={(e) =>
                    setFormData({ ...formData, blogSeoTitle: e.target.value })
                  }
                  maxLength="70"
                />
              </div>
              <div className="form-group">
                <label>Meta Description</label>
                <textarea
                  value={formData.blogSeoDescription}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      blogSeoDescription: e.target.value,
                    })
                  }
                  rows="3"
                  maxLength="160"
                />
              </div>
            </div>
          </aside>
        </div>

        <div
          className="form-actions"
          style={{ marginTop: "20px", display: "flex", gap: "10px" }}
        >
          <button type="button" onClick={closeForm} className="btn-cancel">
            Cancelar
          </button>
          <button type="submit" disabled={submitting} className="btn-save">
            {submitting
              ? "Guardando..."
              : blogData
                ? "Actualizar Artículo"
                : "Publicar Artículo"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default BlogForm;
